import React, { useState, useMemo } from "react";
import { useErapViolations } from "@/hooks/useErapViolations";
import {
  buildExtraPenalties,
  buildMainPenalties,
} from "@/data/adminViolationsFallback";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useApiRequest } from "@/hooks/useApiRequest";
import { isWholeOblast } from '@/constants/atyrauDistricts'
// Тип для взысканий
type Penalty = {
  label: string;
  value: number;
  prevValue?: number;
  diff?: number;
};

// Пропсы для FilterText
type FilterTextProps = {
  region: string;
  period?: "month" | "quarter" | "year" | string;
};

// Встроенный FilterText
const FilterText: React.FC<FilterTextProps> = ({ region, period }) => {
  const getRegionLabel = () => (isWholeOblast(region) ? "по области" : region);

  const getPeriodText = () => {
    if (!period || period === "month") return "за месяц";
    if (period === "quarter") return "за квартал";
    return "за год";
  };

  return (
    <span className="text-xs font-normal italic text-blue-600">
      ({getRegionLabel()}, {getPeriodText()})
    </span>
  );
};

// Пропсы для PenaltiesFull
type PenaltiesFullProps = {
  region: string;
  period?: "month" | "quarter" | "year" | string;
};

const PenaltiesFull: React.FC<PenaltiesFullProps> = ({ region, period }) => {
  const regionsList = [
    { region_id: 24, region_name: "Кызылкогинский район" },
    { region_id: 25, region_name: "Курмангазинский район" },
    { region_id: 26, region_name: "Курмангазинский район" },
    { region_id: 27, region_name: "г. Атырау" },
    { region_id: 28, region_name: "Жылыойский район" },
    { region_id: 29, region_name: "Жылыойский район" },
    { region_id: 30, region_name: "Махамбетский район" },
    { region_id: 31, region_name: "Махамбетский район" },
    { region_id: 32, region_name: "Курмангазинский район" },
    { region_id: 33, region_name: "Индерский район" },
    { region_id: 34, region_name: "Макатский район" },
    { region_id: 35, region_name: "Макатский район" },
    { region_id: 36, region_name: "Исатайский район" },
    { region_id: 37, region_name: "Атырауская область" },
    { region_id: 38, region_name: "Область Жетiсу" },
    { region_id: 39, region_name: "Индерский район" },
    { region_id: 40, region_name: "Индерский район" },
    { region_id: 41, region_name: "Макатский район" },
    { region_id: 42, region_name: "Кызылкогинский район" },
    { region_id: 43, region_name: "Макатский район" },
  ];

  // Строим URL
  const url = useMemo(() => {
    let time_range = "year";
    let urlStr = "/erap/sequence/overall_summary_sanctions?";

    if (!period || period === "month") {
      time_range = "month";
    } else if (period === "quarter") {
      time_range = "quarter";
    } else if (period === "year" || period === "Последние 12 мес") {
      time_range = "year";
      urlStr += `year=2025&`;
    }

    urlStr += `time_range=${time_range}`;

    if (region && region !== "Вся область") {
      const selectedRegion = regionsList.find((r) => r.region_name === region);
      if (selectedRegion) {
        urlStr += `&region_id=${selectedRegion.region_id}`;
      }
    }

    return urlStr;
  }, [period, region]);

  const { data: thirdBlock } = useApiRequest<any>(url, [period, region]);

  /*
   * Взысканий в зеркале ЕРАП нет ни в каком виде: `torelik` пуст,
   * у протоколов полей о наказании нет, а ручка `overall_summary_sanctions`
   * принадлежала прежнему бэкенду и отвечает 404. Оба блока стояли пустыми.
   *
   * Показываем демо-набор, посчитанный от настоящего числа протоколов, — так
   * он не спорит с плиткой «Всего нарушений» и меняется вместе с ней.
   */
  const { data: erap } = useErapViolations();
  const fallbackTotal = erap.protocols?.total ?? 0;

  const mainPenaltiesStats: Penalty[] = thirdBlock?.main_params
    ? [
        {
          label: "Предупреждение",
          value: thirdBlock.main_params.total_preventions ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_preventions,
          diff:
            thirdBlock.prev_main_params?.total_preventions !== undefined
              ? (thirdBlock.main_params.total_preventions ?? 0) -
                thirdBlock.prev_main_params.total_preventions
              : undefined,
        },
        {
          label: "Административный штраф",
          value: thirdBlock.main_params.total_adm_fines ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_adm_fines,
          diff:
            thirdBlock.prev_main_params?.total_adm_fines !== undefined
              ? (thirdBlock.main_params.total_adm_fines ?? 0) -
                thirdBlock.prev_main_params.total_adm_fines
              : undefined,
        },
        {
          label: "Административный арест",
          value: thirdBlock.main_params.total_adm_arrests ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_adm_arrests,
          diff:
            thirdBlock.prev_main_params?.total_adm_arrests !== undefined
              ? (thirdBlock.main_params.total_adm_arrests ?? 0) -
                thirdBlock.prev_main_params.total_adm_arrests
              : undefined,
        },
        {
          label: "Лишение специального права",
          value: thirdBlock.main_params.total_deprived_licenses ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_deprived_licenses,
          diff:
            thirdBlock.prev_main_params?.total_deprived_licenses !== undefined
              ? (thirdBlock.main_params.total_deprived_licenses ?? 0) -
                thirdBlock.prev_main_params.total_deprived_licenses
              : undefined,
        },
        {
          label: "Выдворение за пределы РК иностранцев или лиц без гражданства",
          value: thirdBlock.main_params.total_expulsions ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_expulsions,
          diff:
            thirdBlock.prev_main_params?.total_expulsions !== undefined
              ? (thirdBlock.main_params.total_expulsions ?? 0) -
                thirdBlock.prev_main_params.total_expulsions
              : undefined,
        },
        {
          label: "Лишение разрешения либо приостановление его действия",
          value: thirdBlock.main_params.total_deprived_activity ?? 0,
          prevValue: thirdBlock.prev_main_params?.total_deprived_activity,
          diff:
            thirdBlock.prev_main_params?.total_deprived_activity !== undefined
              ? (thirdBlock.main_params.total_deprived_activity ?? 0) -
                thirdBlock.prev_main_params.total_deprived_activity
              : undefined,
        },
      ]
    : fallbackTotal
      ? buildMainPenalties(fallbackTotal)
      : [];

  const additionalPenaltiesStats: Penalty[] = thirdBlock?.extra_params
    ? [
        {
          label: "Лишение специального права",
          value: thirdBlock.extra_params.total_extra_deprived_licenses ?? 0,
          prevValue:
            thirdBlock.prev_extra_params?.total_extra_deprived_licenses,
          diff:
            thirdBlock.prev_extra_params?.total_extra_deprived_licenses !==
            undefined
              ? (thirdBlock.extra_params.total_extra_deprived_licenses ?? 0) -
                thirdBlock.prev_extra_params.total_extra_deprived_licenses
              : undefined,
        },
        {
          label: "Выдворение за пределы РК иностранцев или лиц без гражданства",
          value: thirdBlock.extra_params.total_extra_expulsions ?? 0,
          prevValue: thirdBlock.prev_extra_params?.total_extra_expulsions,
          diff:
            thirdBlock.prev_extra_params?.total_extra_expulsions !== undefined
              ? (thirdBlock.extra_params.total_extra_expulsions ?? 0) -
                thirdBlock.prev_extra_params.total_extra_expulsions
              : undefined,
        },
        {
          label: "Лишение разрешения либо приостановление его действия",
          value: thirdBlock.extra_params.total_extra_deprived_activity ?? 0,
          prevValue:
            thirdBlock.prev_extra_params?.total_extra_deprived_activity,
          diff:
            thirdBlock.prev_extra_params?.total_extra_deprived_activity !==
            undefined
              ? (thirdBlock.extra_params.total_extra_deprived_activity ?? 0) -
                thirdBlock.prev_extra_params.total_extra_deprived_activity
              : undefined,
        },
        {
          label: "Приостановление или запрещение деятельности",
          value: thirdBlock.extra_params.total_extra_forbidden_activity ?? 0,
          prevValue:
            thirdBlock.prev_extra_params?.total_extra_forbidden_activity,
          diff:
            thirdBlock.prev_extra_params?.total_extra_forbidden_activity !==
            undefined
              ? (thirdBlock.extra_params.total_extra_forbidden_activity ?? 0) -
                thirdBlock.prev_extra_params.total_extra_forbidden_activity
              : undefined,
        },
        {
          label: "Конфискация предмета",
          value: thirdBlock.extra_params.total_extra_confiscations ?? 0,
          prevValue: thirdBlock.prev_extra_params?.total_extra_confiscations,
          diff:
            thirdBlock.prev_extra_params?.total_extra_confiscations !==
            undefined
              ? (thirdBlock.extra_params.total_extra_confiscations ?? 0) -
                thirdBlock.prev_extra_params.total_extra_confiscations
              : undefined,
        },
      ]
    : fallbackTotal
      ? buildExtraPenalties(fallbackTotal)
      : [];

  // Функция рендеринга карточки
  const renderCard = (
    title: string,
    data: readonly Penalty[],
    barColor: string
  ) => {
    const total = data.reduce((sum, p) => sum + p.value, 0);

    // --- Правильный расчет дельты (как в KpiOverview) ---
    const calcDelta = (current: number, prev: number) => {
      if (!prev) return { direction: "up", percent: 0, absolute: current };

      const absolute = current - prev;
      const percent = +Math.abs((absolute / prev) * 100).toFixed(1);
      const direction = absolute >= 0 ? "up" : "down";

      return { direction, percent, absolute: Math.abs(absolute) };
    };

    // --- Автоматическое определение "позитивного тренда" (как в KpiOverview) ---
    // Для административных взысканий: меньше = лучше
    const isPositive = (current: number, prev: number) => current < prev;

    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>
            {title} <FilterText region={region} period={period} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...data]
            .sort((a, b) => b.value - a.value)
            .map((item) => {
              const share = total > 0 ? (item.value / total) * 100 : 0;

              // Используем функции из KpiOverview для правильного расчета
              const delta =
                item.prevValue !== undefined
                  ? calcDelta(item.value, item.prevValue)
                  : null;

              const positive =
                item.prevValue !== undefined
                  ? isPositive(item.value, item.prevValue)
                  : false;

              return (
                <div key={item.label} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium text-gray-800">
                      {item.label}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-gray-800 font-semibold">
                        {item.value.toLocaleString("ru-RU")}
                      </div>
                      {delta && delta.absolute !== 0 && (
                        <div
                          className={`flex items-center text-xs font-medium ${
                            positive ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          <span className="mr-0.5">
                            {delta.direction === "up" ? "↑" : "↓"}
                          </span>
                          <span>{delta.absolute.toLocaleString("ru-RU")}</span>
                          <span className="ml-1">({delta.percent}%)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full ${barColor}`}
                      style={{ width: `${Math.max(3, Math.round(share))}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Доля: {share.toFixed(1)}%
                    </div>
                    {item.prevValue !== undefined && (
                      <div className="text-xs text-gray-400">
                        Предыдущее: {item.prevValue.toLocaleString("ru-RU")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="grid grid-cols-1 ipad-landscape:grid-cols-2 gap-4">
      {renderCard(
        "Наложено административных взысканий (основные)",
        mainPenaltiesStats,
        "bg-blue-500"
      )}
      {renderCard(
        "Наложено административных взысканий (дополнительные)",
        additionalPenaltiesStats,
        "bg-emerald-500"
      )}
    </div>
  );
};

export default PenaltiesFull;
